import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Slider, IconButton, Button } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StraightenIcon from '@mui/icons-material/Straighten';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import * as dicomParser from 'dicom-parser';
import JSZip from 'jszip';

const t = (key, fallback) => fallback || key;

const CANVAS_SIZE = 512;

const WL_PRESETS = {
    'Bone': [400, 2000],
    'Soft Tissue': [40, 400],
    'Dental': [1500, 3500],
    'Air': [-500, 1500],
};

const isDcmFile = (name) => {
    const l = name.toLowerCase();
    return l.endsWith('.dcm') || l.endsWith('.ima') || (!l.includes('.') && !l.endsWith('/'));
};

function canvasCoordsFromEvent(canvas, e, zoom = 1, panX = 0, panY = 0) {
    const rect = canvas.getBoundingClientRect();
    const cw = canvas.width, ch = canvas.height;
    const canvasAspect = cw / ch;
    const containerAspect = rect.width / rect.height;

    let renderWidth, renderHeight, offsetX, offsetY;
    if (containerAspect > canvasAspect) {
        renderHeight = rect.height;
        renderWidth = rect.height * canvasAspect;
        offsetX = (rect.width - renderWidth) / 2;
        offsetY = 0;
    } else {
        renderWidth = rect.width;
        renderHeight = rect.width / canvasAspect;
        offsetX = 0;
        offsetY = (rect.height - renderHeight) / 2;
    }

    const rawX = ((e.clientX - rect.left - offsetX) / renderWidth) * cw;
    const rawY = ((e.clientY - rect.top - offsetY) / renderHeight) * ch;

    const half = cw / 2;
    const x = (rawX - half - panX) / zoom + half;
    const y = (rawY - half - panY) / zoom + half;

    if (x < 0 || x >= cw || y < 0 || y >= ch) return null;
    return { x, y };
}

function getCanvasDisplaySize(canvas) {
    const rect = canvas.getBoundingClientRect();
    return Math.min(rect.width, rect.height);
}

function calculateDistanceMm(p1, p2, plane, vol) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    let dxMm, dyMm;

    if (plane === 'axial') {
        dxMm = (dx / CANVAS_SIZE) * vol.cols * vol.pixelSpacing[1];
        dyMm = (dy / CANVAS_SIZE) * vol.rows * vol.pixelSpacing[0];
    } else if (plane === 'sagittal') {
        dxMm = (dx / CANVAS_SIZE) * vol.slices * vol.sliceSpacing;
        dyMm = (dy / CANVAS_SIZE) * vol.rows * vol.pixelSpacing[0];
    } else {
        dxMm = (dx / CANVAS_SIZE) * vol.cols * vol.pixelSpacing[1];
        dyMm = (dy / CANVAS_SIZE) * vol.slices * vol.sliceSpacing;
    }

    return Math.sqrt(dxMm * dxMm + dyMm * dyMm);
}

function drawMeasurementOverlay(ctx, measurements, pendingPoint, cursorPos, pendingDistanceMm, zoom = 1, panX = 0, panY = 0) {
    const cw = ctx.canvas.width, ch = ctx.canvas.height;
    const half = cw / 2;
    const iz = 1 / zoom;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    ctx.translate(half + panX, half + panY);
    ctx.scale(zoom, zoom);
    ctx.translate(-half, -half);

    for (const m of measurements) {
        ctx.beginPath();
        ctx.moveTo(m.p1.x, m.p1.y);
        ctx.lineTo(m.p2.x, m.p2.y);
        ctx.strokeStyle = '#ffeb3b';
        ctx.lineWidth = 1.5 * iz;
        ctx.stroke();

        for (const p of [m.p1, m.p2]) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 3 * iz, 0, Math.PI * 2);
            ctx.fillStyle = '#ffeb3b';
            ctx.fill();
        }

        const midX = (m.p1.x + m.p2.x) / 2;
        const midY = (m.p1.y + m.p2.y) / 2;
        const label = `${m.distanceMm.toFixed(2)} mm`;
        ctx.font = `bold ${12 * iz}px sans-serif`;
        ctx.textBaseline = 'middle';
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(midX - tw / 2 - 4 * iz, midY - 10 * iz, tw + 8 * iz, 20 * iz);
        ctx.fillStyle = '#ffeb3b';
        ctx.fillText(label, midX - tw / 2, midY);
    }

    if (pendingPoint) {
        ctx.beginPath();
        ctx.arc(pendingPoint.x, pendingPoint.y, 4 * iz, 0, Math.PI * 2);
        ctx.fillStyle = '#ffeb3b';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1 * iz;
        ctx.stroke();

        if (cursorPos) {
            ctx.beginPath();
            ctx.moveTo(pendingPoint.x, pendingPoint.y);
            ctx.lineTo(cursorPos.x, cursorPos.y);
            ctx.strokeStyle = 'rgba(255,235,59,0.6)';
            ctx.lineWidth = 1 * iz;
            ctx.setLineDash([4 * iz, 4 * iz]);
            ctx.stroke();
            ctx.setLineDash([]);

            if (pendingDistanceMm !== null) {
                const mx = (pendingPoint.x + cursorPos.x) / 2;
                const my = (pendingPoint.y + cursorPos.y) / 2;
                const lbl = `${pendingDistanceMm.toFixed(2)} mm`;
                ctx.font = `bold ${11 * iz}px sans-serif`;
                ctx.textBaseline = 'middle';
                const w = ctx.measureText(lbl).width;
                ctx.fillStyle = 'rgba(0,0,0,0.5)';
                ctx.fillRect(mx - w / 2 - 3 * iz, my - 9 * iz, w + 6 * iz, 18 * iz);
                ctx.fillStyle = 'rgba(255,235,59,0.8)';
                ctx.fillText(lbl, mx - w / 2, my);
            }
        }
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
}

async function getCBCTFolder(zipName) {
    const root = await navigator.storage.getDirectory();
    const cbctDir = await root.getDirectoryHandle('cbct', { create: true });
    return cbctDir.getDirectoryHandle(zipName, { create: true });
}

async function folderFileCount(dir) {
    let count = 0;
    for await (const _ of dir.values()) count++;
    return count;
}

async function extractZipToLocal(file, onProgress) {
    const folderName = file.name.replace(/\.zip$/i, '');
    const dir = await getCBCTFolder(folderName);

    const existing = await folderFileCount(dir);
    if (existing > 0) {
        onProgress(100, `Cached (${existing} files) — loading from local storage...`);
        return dir;
    }

    onProgress(0, 'Reading ZIP...');
    const buf = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buf);
    const entries = Object.entries(zip.files).filter(
        ([name, f]) => !f.dir && isDcmFile(name),
    );

    if (entries.length === 0) throw new Error('No DICOM files found in ZIP');

    for (let i = 0; i < entries.length; i++) {
        const [name, entry] = entries[i];
        const dcmBuf = await entry.async('arraybuffer');
        const fileName = `${String(i).padStart(5, '0')}_${name.replace(/.*\//, '')}`;
        const fh = await dir.getFileHandle(fileName, { create: true });
        const writable = await fh.createWritable();
        await writable.write(dcmBuf);
        await writable.close();
        onProgress(Math.round(((i + 1) / entries.length) * 100), `Extracting ${i + 1}/${entries.length}...`);
    }

    return dir;
}

function parseDicomSlice(buf) {
    try {
        const ds = dicomParser.parseDicom(new Uint8Array(buf));

        const rows = ds.uint16('x00280010') ?? 0;
        const cols = ds.uint16('x00280011') ?? 0;
        if (rows === 0 || cols === 0) return null;

        const bitsAllocated = ds.uint16('x00280100') ?? 16;
        const pixelRepresentation = ds.uint16('x00280103') ?? 0;
        const rescaleSlope = parseFloat(ds.string('x00281053') ?? '1');
        const rescaleIntercept = parseFloat(ds.string('x00281052') ?? '0');
        const windowCenter = parseFloat((ds.string('x00281050') ?? '400').split('\\')[0]);
        const windowWidth = parseFloat((ds.string('x00281051') ?? '2000').split('\\')[0]);

        const psStr = ds.string('x00280030') ?? '1\\1';
        const psParts = psStr.split('\\');
        const pixelSpacing = [parseFloat(psParts[0]) || 1, parseFloat(psParts[1] ?? psParts[0]) || 1];
        const sliceThickness = parseFloat(ds.string('x00180050') ?? '1');

        const posStr = ds.string('x00200032');
        let position = 0;
        if (posStr) {
            const parts = posStr.split('\\');
            position = parseFloat(parts[2]) || 0;
        } else {
            position = parseFloat(ds.string('x00201041') ?? '0');
        }
        const instanceNumber = ds.intString('x00200013') ?? 0;

        const pixelDataElement = ds.elements['x7fe00010'];
        if (!pixelDataElement) return null;

        const pixelDataOffset = pixelDataElement.dataOffset;
        const numPixels = rows * cols;
        let pixelData;

        if (bitsAllocated === 16) {
            const raw = new DataView(buf, pixelDataOffset, numPixels * 2);
            pixelData = new Int16Array(numPixels);
            for (let i = 0; i < numPixels; i++) {
                pixelData[i] = raw.getInt16(i * 2, true);
            }
        } else if (bitsAllocated === 8) {
            const raw = new Uint8Array(buf, pixelDataOffset, numPixels);
            pixelData = new Int16Array(numPixels);
            for (let i = 0; i < numPixels; i++) pixelData[i] = raw[i];
        } else {
            return null;
        }

        return {
            slice: { position, instanceNumber, pixelData },
            rows, cols, bitsAllocated, pixelRepresentation,
            rescaleSlope, rescaleIntercept, windowCenter, windowWidth,
            pixelSpacing, sliceThickness,
        };
    } catch {
        return null;
    }
}

async function buildVolumeFromFolder(dir, onProgress) {
    const fileHandles = [];
    for await (const [, handle] of dir.entries()) {
        if (handle.kind === 'file') fileHandles.push(handle);
    }

    if (fileHandles.length === 0) throw new Error('No files in local CBCT folder');

    const sliceResults = [];
    for (let i = 0; i < fileHandles.length; i++) {
        const f = await fileHandles[i].getFile();
        const buf = await f.arrayBuffer();
        const result = parseDicomSlice(buf);
        if (result) sliceResults.push(result);
        onProgress(Math.round(((i + 1) / fileHandles.length) * 100), `Parsing DICOM ${i + 1}/${fileHandles.length}...`);
    }

    if (sliceResults.length === 0) throw new Error('No valid DICOM slices found');

    const meta = sliceResults[0];
    const { rows, cols, rescaleSlope, rescaleIntercept, windowCenter, windowWidth, pixelSpacing, sliceThickness } = meta;

    sliceResults.sort((a, b) => {
        const posDiff = a.slice.position - b.slice.position;
        return posDiff !== 0 ? posDiff : a.slice.instanceNumber - b.slice.instanceNumber;
    });

    let sliceSpacing = sliceThickness;
    if (sliceResults.length > 1) {
        const diff = Math.abs(sliceResults[1].slice.position - sliceResults[0].slice.position);
        if (diff > 0) sliceSpacing = diff;
    }

    const numSlices = sliceResults.length;
    const volume = new Int16Array(numSlices * rows * cols);

    for (let s = 0; s < numSlices; s++) {
        const pd = sliceResults[s].slice.pixelData;
        const offset = s * rows * cols;
        for (let i = 0; i < rows * cols; i++) {
            volume[offset + i] = Math.round(pd[i] * rescaleSlope + rescaleIntercept);
        }
    }

    return {
        volume, rows, cols, slices: numSlices,
        pixelSpacing, sliceSpacing,
        windowCenter, windowWidth,
        rescaleSlope, rescaleIntercept,
    };
}

function renderSliceOffscreen(plane, vol, sliceIdx, wc, ww, invert) {
    const { volume, rows, cols, slices } = vol;
    const lower = wc - ww / 2;
    const upper = wc + ww / 2;

    let imgW, imgH;
    if (plane === 'axial') { imgW = cols; imgH = rows; }
    else if (plane === 'sagittal') { imgW = slices; imgH = rows; }
    else { imgW = cols; imgH = slices; }

    const imgData = new ImageData(imgW, imgH);
    const data = imgData.data;

    if (plane === 'axial') {
        const offset = sliceIdx * rows * cols;
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const hu = volume[offset + r * cols + c];
                let val = ((hu - lower) / (upper - lower)) * 255;
                val = val < 0 ? 0 : val > 255 ? 255 : val;
                if (invert) val = 255 - val;
                const idx = (r * cols + c) * 4;
                data[idx] = data[idx + 1] = data[idx + 2] = val;
                data[idx + 3] = 255;
            }
        }
    } else if (plane === 'sagittal') {
        for (let r = 0; r < rows; r++) {
            for (let s = 0; s < slices; s++) {
                const hu = volume[s * rows * cols + r * cols + sliceIdx];
                let val = ((hu - lower) / (upper - lower)) * 255;
                val = val < 0 ? 0 : val > 255 ? 255 : val;
                if (invert) val = 255 - val;
                const idx = (r * slices + s) * 4;
                data[idx] = data[idx + 1] = data[idx + 2] = val;
                data[idx + 3] = 255;
            }
        }
    } else {
        for (let s = 0; s < slices; s++) {
            for (let c = 0; c < cols; c++) {
                const hu = volume[s * rows * cols + sliceIdx * cols + c];
                let val = ((hu - lower) / (upper - lower)) * 255;
                val = val < 0 ? 0 : val > 255 ? 255 : val;
                if (invert) val = 255 - val;
                const idx = (s * cols + c) * 4;
                data[idx] = data[idx + 1] = data[idx + 2] = val;
                data[idx + 3] = 255;
            }
        }
    }

    const offscreen = new OffscreenCanvas(imgW, imgH);
    const octx = offscreen.getContext('2d');
    octx.putImageData(imgData, 0, 0);
    return offscreen;
}

const MPRView = ({
    label, plane, vol, sliceIdx, maxSlice, onSliceChange,
    wc, ww, onWindowChange, invert, color, isFullscreen, onToggleFullscreen,
    measureMode, measurements, pendingPoint, onMeasureClick,
    resetTrigger,
}) => {
    const canvasRef = useRef(null);
    const overlayRef = useRef(null);
    const wlDragRef = useRef(null);
    const panDragRef = useRef(null);
    const [cursorPos, setCursorPos] = useState(null);

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });

    const cachedSliceRef = useRef(null);

    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [resetTrigger]);

    const pendingDistanceMm = useMemo(() => {
        if (!pendingPoint || !cursorPos) return null;
        return calculateDistanceMm(pendingPoint, cursorPos, plane, vol);
    }, [pendingPoint, cursorPos, plane, vol]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const cw = canvas.width, ch = canvas.height;
        const half = cw / 2;

        const cacheKey = `${plane}-${sliceIdx}-${wc}-${ww}-${invert}`;
        if (!cachedSliceRef.current || cachedSliceRef.current.key !== cacheKey) {
            cachedSliceRef.current = { key: cacheKey, offscreen: renderSliceOffscreen(plane, vol, sliceIdx, wc, ww, invert) };
        }

        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, cw, ch);
        ctx.translate(half + pan.x, half + pan.y);
        ctx.scale(zoom, zoom);
        ctx.translate(-half, -half);
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(cachedSliceRef.current.offscreen, 0, 0, cw, ch);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }, [vol, sliceIdx, wc, ww, invert, plane, zoom, pan]);

    useEffect(() => {
        const canvas = overlayRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        drawMeasurementOverlay(ctx, measurements, pendingPoint, cursorPos, pendingDistanceMm, zoom, pan.x, pan.y);
    }, [measurements, pendingPoint, cursorPos, pendingDistanceMm, zoom, pan]);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
            const canvas = overlayRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const displaySize = getCanvasDisplaySize(canvas);
            const containerAspect = rect.width / rect.height;
            const ox = containerAspect > 1 ? (rect.width - displaySize) / 2 : 0;
            const oy = containerAspect > 1 ? 0 : (rect.height - displaySize) / 2;
            const rawX = ((e.clientX - rect.left - ox) / displaySize) * CANVAS_SIZE;
            const rawY = ((e.clientY - rect.top - oy) / displaySize) * CANVAS_SIZE;

            const factor = e.deltaY > 0 ? 0.9 : 1.1;
            setZoom(prev => {
                const newZoom = Math.max(0.5, Math.min(10, prev * factor));
                const scale = newZoom / prev;
                const half = CANVAS_SIZE / 2;
                setPan(p => ({
                    x: (rawX - half) * (1 - scale) + p.x * scale,
                    y: (rawY - half) * (1 - scale) + p.y * scale,
                }));
                return newZoom;
            });
        } else {
            const delta = e.deltaY > 0 ? 1 : -1;
            onSliceChange(Math.max(0, Math.min(maxSlice, sliceIdx + delta)));
        }
    }, [sliceIdx, maxSlice, onSliceChange]);

    const handleMouseDown = useCallback((e) => {
        if (e.button === 2) {
            e.preventDefault();
            wlDragRef.current = { startX: e.clientX, startY: e.clientY, startWC: wc, startWW: ww };
        } else if (e.button === 0) {
            if (measureMode) {
                const canvas = overlayRef.current;
                if (!canvas) return;
                const pt = canvasCoordsFromEvent(canvas, e, zoom, pan.x, pan.y);
                if (pt) onMeasureClick(pt);
            } else {
                panDragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
            }
        } else if (e.button === 1) {
            e.preventDefault();
            panDragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
        }
    }, [wc, ww, measureMode, onMeasureClick, zoom, pan]);

    const handleMouseMove = useCallback((e) => {
        if (wlDragRef.current) {
            const dx = e.clientX - wlDragRef.current.startX;
            const dy = e.clientY - wlDragRef.current.startY;
            const newWW = Math.max(1, wlDragRef.current.startWW + dx * 4);
            const newWC = wlDragRef.current.startWC - dy * 4;
            onWindowChange(newWC, newWW);
            return;
        }
        if (panDragRef.current) {
            const canvas = overlayRef.current;
            if (!canvas) return;
            const displaySize = getCanvasDisplaySize(canvas);
            const dx = e.clientX - panDragRef.current.startX;
            const dy = e.clientY - panDragRef.current.startY;
            setPan({
                x: panDragRef.current.startPanX + dx * (CANVAS_SIZE / displaySize),
                y: panDragRef.current.startPanY + dy * (CANVAS_SIZE / displaySize),
            });
            return;
        }
        if (measureMode && pendingPoint) {
            const canvas = overlayRef.current;
            if (!canvas) return;
            setCursorPos(canvasCoordsFromEvent(canvas, e, zoom, pan.x, pan.y));
        }
    }, [onWindowChange, measureMode, pendingPoint, zoom, pan]);

    const handleMouseUp = useCallback(() => {
        wlDragRef.current = null;
        panDragRef.current = null;
    }, []);

    const handleMouseLeave = useCallback(() => {
        wlDragRef.current = null;
        panDragRef.current = null;
        setCursorPos(null);
    }, []);

    const handleDoubleClick = useCallback(() => {
        if (measureMode) return;
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [measureMode]);

    const cursorStyle = measureMode ? 'crosshair' : panDragRef.current ? 'grabbing' : zoom > 1 ? 'grab' : 'default';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
                    {label}
                </Typography>
                {zoom !== 1 && (
                    <Typography sx={{ fontSize: 9, color: '#ffeb3b', fontWeight: 600 }}>
                        {Math.round(zoom * 100)}%
                    </Typography>
                )}
                <Typography sx={{ fontSize: 10, color: 'var(--text-3)' }}>
                    {sliceIdx + 1}/{maxSlice + 1}
                </Typography>
                <IconButton size="small" onClick={onToggleFullscreen}
                    sx={{ color: 'var(--text-3)', '&:hover': { color: 'var(--text-1)' }, p: 0.25 }}>
                    {isFullscreen
                        ? <FullscreenExitIcon sx={{ fontSize: 16 }} />
                        : <FullscreenIcon sx={{ fontSize: 16 }} />}
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                <Box sx={{ flex: 1, position: 'relative', minHeight: 0, bgcolor: '#000' }}>
                    <canvas
                        ref={canvasRef}
                        data-role="slice"
                        width={CANVAS_SIZE}
                        height={CANVAS_SIZE}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                    <canvas
                        ref={overlayRef}
                        data-role="overlay"
                        width={CANVAS_SIZE}
                        height={CANVAS_SIZE}
                        style={{
                            width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                            position: 'absolute', top: 0, left: 0,
                            cursor: cursorStyle,
                        }}
                        onWheel={handleWheel}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onDoubleClick={handleDoubleClick}
                        onContextMenu={e => e.preventDefault()}
                    />
                </Box>

                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', width: 22, py: 1, flexShrink: 0,
                    bgcolor: 'rgba(0,0,0,0.3)',
                }}>
                    <ZoomInIcon sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, mb: 0.5 }} />
                    <Slider
                        orientation="vertical"
                        size="small"
                        min={50}
                        max={500}
                        step={10}
                        value={Math.round(zoom * 100)}
                        onChange={(_, v) => setZoom(v / 100)}
                        sx={{
                            flex: 1, color: 'rgba(255,255,255,0.35)',
                            '& .MuiSlider-thumb': { width: 8, height: 8 },
                            '& .MuiSlider-rail': { opacity: 0.2 },
                            '& .MuiSlider-track': { border: 'none' },
                        }}
                    />
                    <Typography sx={{ fontSize: 7, color: 'rgba(255,255,255,0.3)', mt: 0.5, userSelect: 'none' }}>
                        {Math.round(zoom * 100)}%
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(0,0,0,0.4)' }}>
                <Slider
                    size="small" min={0} max={maxSlice} value={sliceIdx}
                    onChange={(_, v) => onSliceChange(v)}
                    sx={{ color, py: 0.5, '& .MuiSlider-thumb': { width: 12, height: 12 } }}
                />
            </Box>
        </Box>
    );
};

const CBCTViewer = ({ file, onScreenshot }) => {
    const viewerRef = useRef(null);
    const [vol, setVol] = useState(null);
    const [phase, setPhase] = useState('loading');
    const [progress, setProgress] = useState(0);
    const [statusLabel, setStatusLabel] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const [axialIdx, setAxialIdx] = useState(0);
    const [sagittalIdx, setSagittalIdx] = useState(0);
    const [coronalIdx, setCoronalIdx] = useState(0);

    const [wc, setWC] = useState(400);
    const [ww, setWW] = useState(2000);
    const [invert, setInvert] = useState(false);

    const [fullscreenPlane, setFullscreenPlane] = useState(null);
    const toggleFullscreen = useCallback((plane) => {
        setFullscreenPlane(prev => prev === plane ? null : plane);
    }, []);

    const [measureMode, setMeasureMode] = useState(false);
    const [measurements, setMeasurements] = useState([]);
    const [pendingMeasure, setPendingMeasure] = useState(null);

    const [resetTrigger, setResetTrigger] = useState(0);

    const toggleMeasureMode = useCallback(() => {
        setMeasureMode(prev => !prev);
        setPendingMeasure(null);
    }, []);

    const handleMeasureClick = useCallback((plane, point) => {
        if (!vol) return;
        if (pendingMeasure && pendingMeasure.plane === plane) {
            const dist = calculateDistanceMm(pendingMeasure.p1, point, plane, vol);
            const m = {
                id: `${Date.now()}`,
                plane,
                p1: pendingMeasure.p1,
                p2: point,
                distanceMm: dist,
            };
            setMeasurements(prev => [...prev, m]);
            setPendingMeasure(null);
        } else {
            setPendingMeasure({ plane, p1: point });
        }
    }, [vol, pendingMeasure]);

    const handleClearMeasurements = useCallback(() => {
        setMeasurements([]);
        setPendingMeasure(null);
    }, []);

    const takeScreenshot = useCallback(() => {
        const el = viewerRef.current;
        if (!el || !onScreenshot) return;
        const sliceCanvases = Array.from(el.querySelectorAll('canvas[data-role="slice"]'));
        const overlayCanvases = Array.from(el.querySelectorAll('canvas[data-role="overlay"]'));
        if (sliceCanvases.length === 0) return;

        const s = CANVAS_SIZE;

        if (sliceCanvases.length === 1) {
            const out = document.createElement('canvas');
            out.width = s; out.height = s;
            const ctx = out.getContext('2d');
            ctx.drawImage(sliceCanvases[0], 0, 0, s, s);
            if (overlayCanvases[0]) ctx.drawImage(overlayCanvases[0], 0, 0, s, s);
            onScreenshot(out.toDataURL('image/png'));
            return;
        }

        const out = document.createElement('canvas');
        out.width = s * 2; out.height = s * 2;
        const ctx = out.getContext('2d');
        ctx.fillStyle = '#0a0a14';
        ctx.fillRect(0, 0, out.width, out.height);
        sliceCanvases.forEach((cv, i) => {
            if (i < 4) {
                const x = (i % 2) * s;
                const y = Math.floor(i / 2) * s;
                ctx.drawImage(cv, x, y, s, s);
                if (overlayCanvases[i]) ctx.drawImage(overlayCanvases[i], x, y, s, s);
            }
        });
        onScreenshot(out.toDataURL('image/png'));
    }, [onScreenshot]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                const dir = await extractZipToLocal(file, (pct, label) => {
                    if (!cancelled) { setProgress(pct); setStatusLabel(label); }
                });
                if (cancelled) return;

                const volume = await buildVolumeFromFolder(dir, (pct, label) => {
                    if (!cancelled) { setProgress(pct); setStatusLabel(label); }
                });
                if (cancelled) return;

                setAxialIdx(Math.floor(volume.slices / 2));
                setSagittalIdx(Math.floor(volume.cols / 2));
                setCoronalIdx(Math.floor(volume.rows / 2));
                setWC(volume.windowCenter);
                setWW(volume.windowWidth);
                setVol(volume);
                setPhase('viewing');
            } catch (e) {
                if (!cancelled) {
                    setPhase('error');
                    setErrorMsg(e?.message || 'Failed to load CBCT');
                }
            }
        }

        load();
        return () => { cancelled = true; };
    }, [file]);

    const handleWindowChange = useCallback((newWC, newWW) => {
        setWC(newWC);
        setWW(newWW);
    }, []);

    const handleReset = useCallback(() => {
        if (!vol) return;
        setAxialIdx(Math.floor(vol.slices / 2));
        setSagittalIdx(Math.floor(vol.cols / 2));
        setCoronalIdx(Math.floor(vol.rows / 2));
        setWC(vol.windowCenter);
        setWW(vol.windowWidth);
        setInvert(false);
        setMeasureMode(false);
        setMeasurements([]);
        setPendingMeasure(null);
        setResetTrigger(prev => prev + 1);
    }, [vol]);

    if (phase === 'loading') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 2, bgcolor: 'var(--bg-base)' }}>
                <Typography sx={{ color: 'var(--text-1)', fontSize: 14, fontWeight: 500 }}>
                    {statusLabel || 'Loading CBCT...'}
                </Typography>
                <Box sx={{ width: 220, height: 6, bgcolor: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{ width: `${progress}%`, height: '100%', bgcolor: '#7b68ee', borderRadius: 3, transition: 'width 0.3s' }} />
                </Box>
                <Typography sx={{ color: 'var(--text-3)', fontSize: 12 }}>{progress}%</Typography>
            </Box>
        );
    }

    if (phase === 'error') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', bgcolor: 'var(--bg-base)' }}>
                <Typography sx={{ color: '#ef4444', fontSize: 14 }}>{errorMsg}</Typography>
            </Box>
        );
    }

    if (!vol) return null;

    const mprMeasureProps = (plane) => ({
        measureMode,
        measurements: measurements.filter(m => m.plane === plane),
        pendingPoint: pendingMeasure?.plane === plane ? pendingMeasure.p1 : null,
        onMeasureClick: (pt) => handleMeasureClick(plane, pt),
        resetTrigger,
    });

    return (
        <Box ref={viewerRef} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'var(--bg-base)', overflow: 'hidden' }}>
            {/* Toolbar */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5,
                bgcolor: 'var(--bg-surface)', borderBottom: '1px solid var(--border)',
                flexShrink: 0, flexWrap: 'wrap',
            }}>
                {Object.entries(WL_PRESETS).map(([name, [c, w]]) => (
                    <Box key={name} onClick={() => { setWC(c); setWW(w); }} sx={{
                        px: 1, py: 0.3, borderRadius: 1, fontSize: 10, cursor: 'pointer',
                        bgcolor: wc === c && ww === w ? '#7b68ee' : 'var(--bg-input)',
                        color: wc === c && ww === w ? '#fff' : 'var(--text-2)',
                        border: `1px solid ${wc === c && ww === w ? '#7b68ee' : 'var(--border)'}`,
                        '&:hover': { borderColor: '#7b68ee' },
                    }}>
                        {name}
                    </Box>
                ))}

                <Box onClick={() => setInvert(v => !v)} sx={{
                    px: 1, py: 0.3, borderRadius: 1, fontSize: 10, cursor: 'pointer',
                    bgcolor: invert ? '#7b68ee' : 'var(--bg-input)',
                    color: invert ? '#fff' : 'var(--text-2)',
                    border: `1px solid ${invert ? '#7b68ee' : 'var(--border)'}`,
                }}>
                    {t('cbctViewer.invert', 'Invert')}
                </Box>

                <Box onClick={toggleMeasureMode} sx={{
                    px: 1, py: 0.3, borderRadius: 1, fontSize: 10, cursor: 'pointer',
                    bgcolor: measureMode ? '#ffeb3b' : 'var(--bg-input)',
                    color: measureMode ? '#000' : 'var(--text-2)',
                    border: `1px solid ${measureMode ? '#ffeb3b' : 'var(--border)'}`,
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    '&:hover': { borderColor: measureMode ? '#ffeb3b' : '#7b68ee' },
                }}>
                    <StraightenIcon sx={{ fontSize: 14 }} />
                    {t('cbctViewer.measure', 'Measure')}
                </Box>

                {measurements.length > 0 && (
                    <Box onClick={handleClearMeasurements} sx={{
                        px: 1, py: 0.3, borderRadius: 1, fontSize: 10, cursor: 'pointer',
                        bgcolor: 'var(--bg-input)', color: '#ffeb3b',
                        border: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        '&:hover': { borderColor: '#ffeb3b' },
                    }}>
                        <DeleteSweepIcon sx={{ fontSize: 14 }} />
                        {t('cbctViewer.clearMeasurements', 'Clear')} ({measurements.length})
                    </Box>
                )}

                {onScreenshot && (
                    <Button
                        size="small"
                        onClick={takeScreenshot}
                        startIcon={<CameraAltIcon sx={{ fontSize: 12 }} />}
                        sx={{
                            color: '#c9a0dc', fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: 0.5, py: 0.3, px: 1.5, minHeight: 0, ml: 'auto',
                            border: '1px solid rgba(201,160,220,0.3)', borderRadius: '6px',
                            '&:hover': { backgroundColor: 'rgba(201,160,220,0.15)', borderColor: 'rgba(201,160,220,0.5)' },
                        }}
                    >
                        {t('cbctViewer.screenshot', 'Screenshot')}
                    </Button>
                )}
                <IconButton size="small" onClick={handleReset}
                    sx={{ color: 'var(--text-3)', '&:hover': { color: 'var(--text-1)' }, ml: onScreenshot ? 0 : 'auto' }}>
                    <RestartAltIcon sx={{ fontSize: 16 }} />
                </IconButton>

                <Typography sx={{ fontSize: 10, color: 'var(--text-3)', mr: 0.5 }}>
                    WC:{Math.round(wc)} WW:{Math.round(ww)}
                </Typography>

                <Typography sx={{ fontSize: 10, color: 'var(--text-3)' }}>
                    {vol.cols}×{vol.rows}×{vol.slices}
                </Typography>
            </Box>

            {fullscreenPlane ? (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {fullscreenPlane === 'axial' && (
                        <MPRView label={t('cbctViewer.axial', 'Axial')} plane="axial" vol={vol} color="#4fc3f7"
                            sliceIdx={axialIdx} maxSlice={vol.slices - 1} onSliceChange={setAxialIdx}
                            wc={wc} ww={ww} onWindowChange={handleWindowChange} invert={invert}
                            isFullscreen onToggleFullscreen={() => toggleFullscreen('axial')}
                            {...mprMeasureProps('axial')} />
                    )}
                    {fullscreenPlane === 'sagittal' && (
                        <MPRView label={t('cbctViewer.sagittal', 'Sagittal')} plane="sagittal" vol={vol} color="#ef5350"
                            sliceIdx={sagittalIdx} maxSlice={vol.cols - 1} onSliceChange={setSagittalIdx}
                            wc={wc} ww={ww} onWindowChange={handleWindowChange} invert={invert}
                            isFullscreen onToggleFullscreen={() => toggleFullscreen('sagittal')}
                            {...mprMeasureProps('sagittal')} />
                    )}
                    {fullscreenPlane === 'coronal' && (
                        <MPRView label={t('cbctViewer.coronal', 'Coronal')} plane="coronal" vol={vol} color="#66bb6a"
                            sliceIdx={coronalIdx} maxSlice={vol.rows - 1} onSliceChange={setCoronalIdx}
                            wc={wc} ww={ww} onWindowChange={handleWindowChange} invert={invert}
                            isFullscreen onToggleFullscreen={() => toggleFullscreen('coronal')}
                            {...mprMeasureProps('coronal')} />
                    )}
                </Box>
            ) : (
                <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '2px', minHeight: 0 }}>
                    <MPRView label={t('cbctViewer.axial', 'Axial')} plane="axial" vol={vol} color="#4fc3f7"
                        sliceIdx={axialIdx} maxSlice={vol.slices - 1} onSliceChange={setAxialIdx}
                        wc={wc} ww={ww} onWindowChange={handleWindowChange} invert={invert}
                        isFullscreen={false} onToggleFullscreen={() => toggleFullscreen('axial')}
                        {...mprMeasureProps('axial')} />
                    <MPRView label={t('cbctViewer.sagittal', 'Sagittal')} plane="sagittal" vol={vol} color="#ef5350"
                        sliceIdx={sagittalIdx} maxSlice={vol.cols - 1} onSliceChange={setSagittalIdx}
                        wc={wc} ww={ww} onWindowChange={handleWindowChange} invert={invert}
                        isFullscreen={false} onToggleFullscreen={() => toggleFullscreen('sagittal')}
                        {...mprMeasureProps('sagittal')} />
                    <MPRView label={t('cbctViewer.coronal', 'Coronal')} plane="coronal" vol={vol} color="#66bb6a"
                        sliceIdx={coronalIdx} maxSlice={vol.rows - 1} onSliceChange={setCoronalIdx}
                        wc={wc} ww={ww} onWindowChange={handleWindowChange} invert={invert}
                        isFullscreen={false} onToggleFullscreen={() => toggleFullscreen('coronal')}
                        {...mprMeasureProps('coronal')} />

                    <Box sx={{
                        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                        bgcolor: 'var(--bg-surface)', gap: 1, p: 2,
                    }}>
                        <Typography sx={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 600 }}>{t('cbctViewer.volume', 'CBCT Volume')}</Typography>
                        <Box sx={{ fontSize: 10, color: 'var(--text-3)', textAlign: 'center', lineHeight: 1.8 }}>
                            <div>{t('cbctViewer.dimensions', 'Dimensions')}: {vol.cols} × {vol.rows} × {vol.slices}</div>
                            <div>{t('cbctViewer.pixelSpacing', 'Pixel Spacing')}: {vol.pixelSpacing[0].toFixed(3)} × {vol.pixelSpacing[1].toFixed(3)} mm</div>
                            <div>{t('cbctViewer.sliceSpacing', 'Slice Spacing')}: {vol.sliceSpacing.toFixed(3)} mm</div>
                            {measurements.length > 0 && (
                                <div style={{ color: '#ffeb3b', marginTop: 4 }}>
                                    {t('cbctViewer.measurementsCount', 'Measurements')}: {measurements.length}
                                </div>
                            )}
                        </Box>
                        <Typography sx={{ fontSize: 9, color: 'var(--text-3)', mt: 2, textAlign: 'center', lineHeight: 1.6 }}>
                            {t('cbctViewer.hintScroll', 'Scroll to navigate slices')}<br />
                            {t('cbctViewer.hintDrag', 'Right-drag to adjust window/level')}<br />
                            {t('cbctViewer.hintZoom', 'Ctrl+scroll to zoom, drag to pan')}
                            {measureMode && <><br />{t('cbctViewer.hintMeasure', 'Left-click to measure distance')}</>}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default CBCTViewer;
